import {
  InfiniteData,
  UseInfiniteQueryOptions,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { showToast } from '../../lib/toast';
import { BlogPreview } from '../../types/blog';

interface FetchArticlesResult {
  blogPosts: BlogPreview[];
  nextPage?: number;
}

interface UseArticlesProps
  extends Omit<
    UseInfiniteQueryOptions<
      FetchArticlesResult, // TQueryFnData
      Error, // TError
      InfiniteData<FetchArticlesResult>, // TData
      FetchArticlesResult, // TQueryData
      [string, (string | number)?], // TQueryKey. First value is the name of the query, second is an optional category id
      number // TPageParam
    >,
    'queryFn' | 'getNextPageParam' | 'refetchOnWindowFocus' | 'throwOnError'
  > {
  perPage: number;
  /** The article id to exclude from data fetching. */
  exclude?: string[];
  /** Comma separated string of category IDs to fetch. */
  categories?: string;
}

export function useInfiniteArticles({
  exclude,
  perPage = 8,
  initialPageParam = 1,
  categories,
  ...queryOptions
}: UseArticlesProps) {
  const fetchArticles = useCallback(
    async ({ pageParam = 1 }) => {
      const [, selectedCategory] = queryOptions.queryKey;

      /**
       * If `selectedCategory` is set, we use that, since that's they query key.
       * If not, we're fetching articles that are within a list of categories.
       */
      let categoriesSearchParam: string | undefined;
      if (selectedCategory) {
        categoriesSearchParam = selectedCategory.toString();
      } else if (categories) {
        categoriesSearchParam = categories;
      }

      const queryParams = new URLSearchParams({
        page: pageParam.toString(),
        per_page: perPage.toString(),
        ...(exclude ? { exclude: exclude.join(',') } : {}),
        ...(categoriesSearchParam ? { categories: categoriesSearchParam } : {}),
      });

      const response = await fetch(`/api/articles?${queryParams}`);

      if (!response.ok) {
        showToast('There was a problem fetching articles. Please try again.', {
          type: 'error',
        });

        throw new Error(
          'There was a problem fetching the articles. Please try again.'
        );
      }

      const {
        blogPosts,
        hasNextPage,
      }: { blogPosts: BlogPreview[]; hasNextPage: boolean } =
        await response.json();

      return {
        blogPosts,
        nextPage: hasNextPage ? pageParam + 1 : undefined,
      };
    },
    [exclude, perPage, queryOptions.queryKey, categories]
  );

  const queryResult = useInfiniteQuery({
    queryFn: fetchArticles,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    refetchOnWindowFocus: false,
    throwOnError: false,
    initialPageParam,
    ...queryOptions,
  });

  return queryResult;
}
